import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, BLOG_CATEGORIES } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Field, inputCls } from "./MediaLibrary";

const SCHEMAS = {
  posts: {
    label: "Journal articles", title: "title",
    blank: { title: "New article", title_fr: "", category: BLOG_CATEGORIES[0], excerpt: "", excerpt_fr: "", body: "", body_fr: "", cover_photo_id: "", cover_url: "", reading_minutes: 5, published: true },
    fields: [["title", "Title (EN)"], ["title_fr", "Title (FR)"], ["slug", "URL slug"], ["category", "Category", "select", BLOG_CATEGORIES], ["cover_photo_id", "Cover photo", "photo"], ["cover_url", "…or cover image URL"], ["excerpt", "Excerpt (EN)", "area"], ["excerpt_fr", "Excerpt (FR)", "area"], ["body", "Body (EN) — blank line = new paragraph, \"> \" = pull quote", "long"], ["body_fr", "Body (FR)", "long"], ["reading_minutes", "Reading minutes", "number"], ["published", "Published", "bool"]],
  },
  videos: {
    label: "Videos", title: "title",
    blank: { title: "New film", title_fr: "", category: "documentary", description: "", description_fr: "", video_url: "", thumbnail_photo_id: "", thumbnail_url: "", duration: "", featured: false, order: 0, published: true },
    fields: [["title", "Title (EN)"], ["title_fr", "Title (FR)"], ["video_url", "YouTube or Vimeo URL"], ["category", "Category", "select", ["documentary", "wildlife", "travel"]], ["description", "Description (EN)", "area"], ["description_fr", "Description (FR)", "area"], ["thumbnail_photo_id", "Thumbnail photo", "photo"], ["thumbnail_url", "…or thumbnail URL (YouTube thumbnail is automatic)"], ["duration", "Duration (e.g. 12:40)"], ["order", "Order", "number"], ["featured", "Featured documentary", "bool"], ["published", "Published", "bool"]],
  },
  instagram: {
    label: "Instagram showcase", title: "caption",
    blank: { photo_id: "", image_url: "", caption: "", permalink: "", order: 0, published: true },
    fields: [["permalink", "Instagram post link — paste e.g. https://www.instagram.com/p/ABC123/ (the real post is shown automatically)"], ["photo_id", "Optional: show a library photo instead of the embed", "photo"], ["image_url", "Optional: …or an image URL instead of the embed"], ["caption", "Caption (admin label)"], ["order", "Order", "number"], ["published", "Published", "bool"]],
  },
};

const FieldInput = ({ def, value, onChange, photos }) => {
  const [k, , type, opts] = def;
  const tid = `content-field-${k}`;
  if (type === "select") return <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>{opts.map((o) => <option key={o}>{o}</option>)}</select>;
  if (type === "photo") return (
    <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">— none —</option>
      {photos.map((p) => <option key={p.id} value={p.id}>{p.category} · {p.title}</option>)}
    </select>
  );
  if (type === "bool") return <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={tid} />;
  if (type === "number") return <input type="number" className={inputCls} value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} data-testid={tid} />;
  if (type === "area" || type === "long") return <textarea rows={type === "long" ? 12 : 3} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  return <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
};

export const ContentAdmin = ({ name, photos }) => {
  const S = SCHEMAS[name];
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get(`/admin/content/${name}`).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); setSel(null); }, [name]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try {
      const r = sel.id ? await api.put(`/admin/content/${name}/${sel.id}`, sel) : await api.post(`/admin/content/${name}`, sel);
      toast.success("Saved"); setSel(r.data); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/content/${name}/${sel.id}`); setSel(null); load(); toast.success("Deleted");
  };
  return (
    <div className="grid gap-6 lg:grid-cols-12" data-testid={`content-admin-${name}`}>
      <div className="lg:col-span-4">
        <button onClick={() => setSel({ ...S.blank })} data-testid={`content-new-${name}`} className="mb-3 flex w-full items-center justify-center gap-2 border border-dashed border-white/25 py-3 text-sm text-white hover:border-white"><Plus size={14} /> New</button>
        <div className="space-y-1">
          {items.map((it) => (
            <button key={it.id} onClick={() => setSel(it)} data-testid={`content-item-${it.id}`}
              className={`block w-full truncate border px-3 py-2.5 text-left text-sm ${sel?.id === it.id ? "border-white bg-white/5 text-white" : "border-white/10 text-white/75 hover:border-white/30"}`}>
              {it[S.title] || it.permalink || "(untitled)"}{it.published === false && <span className="ml-2 font-mono text-[10px] text-white/50">draft</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-8">
        {sel ? (
          <div className="space-y-4 border border-white/10 bg-[#0f0f0f] p-5">
            {S.fields.map((def) => (
              <Field key={def[0]} label={def[1]}><FieldInput def={def} value={sel[def[0]]} photos={photos} onChange={(v) => setSel({ ...sel, [def[0]]: v })} /></Field>
            ))}
            <div className="flex gap-2 pt-2">
              <button onClick={save} data-testid="content-save-button" className="flex items-center gap-2 bg-white px-5 py-2.5 text-sm font-semibold text-black"><Save size={14} /> Save</button>
              {sel.id && <button onClick={remove} data-testid="content-delete-button" className="flex items-center gap-2 border border-red-500/50 px-4 py-2.5 text-sm text-red-400"><Trash2 size={14} /> Delete</button>}
            </div>
          </div>
        ) : <p className="text-sm text-white/55">Select an item or create a new one.</p>}
      </div>
    </div>
  );
};

const SETTINGS_FIELDS = [["instagram_handle", "Instagram account (without @)"], ["contact_email", "Contact email"], ["youtube_url", "YouTube channel URL"], ["vimeo_url", "Vimeo URL"],
  ["hero_kicker", "Hero kicker (EN)"], ["hero_kicker_fr", "Hero kicker (FR)"], ["hero_title", "Hero title (EN)"], ["hero_title_fr", "Hero title (FR)"], ["hero_subtitle", "Hero subtitle (EN)"], ["hero_subtitle_fr", "Hero subtitle (FR)"],
  ["about_title", "About title (EN)"], ["about_title_fr", "About title (FR)"], ["about_body", "About text (EN)", "long"], ["about_body_fr", "About text (FR)", "long"], ["about_photo_id", "About page photo", "photo"]];

export const SettingsAdmin = ({ photos }) => {
  const { setSettings } = useLang();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const save = async () => {
    try { const r = await api.put("/admin/settings", s); setSettings(r.data); toast.success("Settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid="settings-admin">
      {SETTINGS_FIELDS.map((def) => <Field key={def[0]} label={def[1]}><FieldInput def={def} value={s[def[0]]} photos={photos} onChange={(v) => setS({ ...s, [def[0]]: v })} /></Field>)}
      <button onClick={save} data-testid="settings-save-button" className="flex items-center gap-2 bg-white px-5 py-2.5 text-sm font-semibold text-black"><Save size={14} /> Save settings</button>
    </div>
  );
};

export const MessagesAdmin = () => {
  const [m, setM] = useState([]);
  const [subs, setSubs] = useState([]);
  const load = () => {
    api.get("/admin/messages").then((r) => setM(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/admin/subscribers").then((r) => setSubs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="grid gap-8 lg:grid-cols-3" data-testid="messages-admin">
      <div className="space-y-3 lg:col-span-2">
        {!m.length && <p className="text-sm text-white/55">No messages yet.</p>}
        {m.map((x) => (
          <div key={x.id} className="border border-white/10 bg-[#0f0f0f] p-4" data-testid={`message-${x.id}`}>
            <div className="flex justify-between gap-3">
              <div className="text-sm text-white">{x.name} · <a className="underline" href={`mailto:${x.email}`}>{x.email}</a></div>
              <button onClick={async () => { await api.delete(`/admin/messages/${x.id}`); load(); }} className="text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
            <div className="mt-1 font-mono text-[11px] text-white/50">{x.kind} · {new Date(x.created_at).toLocaleString()} · {x.subject}</div>
            <p className="mt-3 whitespace-pre-line text-sm text-white/80">{x.message}</p>
          </div>
        ))}
      </div>
      <div>
        <div className="kicker mb-3 flex items-center gap-2"><Mail size={12} /> Newsletter ({subs.length})</div>
        <div className="space-y-1 font-mono text-xs text-white/75">{subs.map((x) => <div key={x.id}>{x.email}</div>)}</div>
      </div>
    </div>
  );
};
